import React, { useState, useEffect } from 'react';
import { Building2, MapPin, ExternalLink } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import API_BASE_URL from '../services/apiconfig';

const Dccwss = () => {
  const { language } = useLanguage();
  const [showBanksList, setShowBanksList] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dccwssData, setDccwssData] = useState(null);

  useEffect(() => {
    fetchDccwssData();
  }, []);

  const fetchDccwssData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/dccwss/get_dccwss_content`);
      const result = await response.json();
      if (result.status === 'success' && result.data) {
        setDccwssData(result.data);
      }
    } catch (error) {
      console.error('Error fetching DCCWSS data:', error);
    } finally {
      setLoading(false);
    }
  };

  // UI labels (static translation strings)
  const content = {
    en: {
      title: "District Central Co-operative Banks",
      about: "Overview",
      operationalNetwork: "Operational Network",
      dccbs: "District Central Co-operative Banks",
      branches: "Branches across Tamil Nadu",
      keyStats: "Key Statistics",
      dccbList: "List of District Central Co-operative Banks",
      visitWebsite: "Visit Website",
      viewList: "Click to view all DCCB Banks",
      servicesOffered: "Services Offered",
      bankingServices: "1. Banking Services",
      loanProducts: "2. Loan Products",
      otherServices: "3. Other Services",
      governmentSchemes: "Government Scheme Loans",
      additionalServices: "Additional Services",
      websiteNotAvailable: "Website not available"
    },
    ta: {
      title: "மாவட்ட மத்திய கூட்டுறவு வங்கிகள்",
      about: "கண்ணோட்டம்",
      operationalNetwork: "செயல்பாட்டு வலையமைப்பு",
      dccbs: "மாவட்ட மத்திய கூட்டுறவு வங்கிகள்",
      branches: "தமிழ்நாடு முழுவதும் கிளைகள்",
      keyStats: "முக்கிய புள்ளிவிவரங்கள்",
      dccbList: "மாவட்ட மத்திய கூட்டுறவு வங்கிகளின் பட்டியல்",
      visitWebsite: "இணையதளத்தைப் பார்வையிடவும்",
      viewList: "அனைத்து DCCB வங்கிகளைப் பார்க்க கிளிக் செய்யவும்",
      servicesOffered: "வழங்கப்படும் சேவைகள்",
      bankingServices: "1. வங்கி சேவைகள்",
      loanProducts: "2. கடன் தயாரிப்புகள்",
      otherServices: "3. பிற சேவைகள்",
      governmentSchemes: "அரசு திட்டக் கடன்கள்",
      additionalServices: "கூடுதல் சேவைகள்",
      websiteNotAvailable: "இணையதளம் கிடைக்கவில்லை"
    }
  };

  const t = content[language];

  const handleBankClick = (website) => {
    if (website && website !== "#") {
      window.open(website, '_blank', 'noopener,noreferrer');
    }
  };

  const pickLang = (item, engKey, taKey) =>
    language === 'en' ? (item[engKey] || '') : (item[taKey] || item[engKey] || '');

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!dccwssData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-xl text-gray-600">No DCCWSS data available</div>
      </div>
    );
  }

  const overview = language === 'en' ? dccwssData.overview_eng : dccwssData.overview_tamil;
  const keyStats = dccwssData.key_stats || [];
  const banksList = dccwssData.banks_list || [];
  const bankingServices = dccwssData.banking_services || [];
  const loanProducts = dccwssData.loan_products || [];
  const governmentSchemes = dccwssData.government_schemes || [];
  const additionalServices = dccwssData.additional_services || [];

  return (
    <div className="min-h-screen bg-gray-50">

      {/* Header */}
      <div className="bg-blue-50 border-b border-blue-100">
        <div className="mx-auto px-4 py-10">
          <h1 className="text-3xl font-bold text-gray-800 text-center">
            {t.title}
          </h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="mx-auto px-4 py-8">
        <section id="dccb-section" className="bg-white p-4 sm:p-6 lg:p-8 rounded-lg shadow-sm">

          {/* About Section */}
          <div id="overview-section" className="mb-10">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">{t.about}</h3>
            <div className="bg-blue-50 p-6 rounded-lg">
              <p className="text-gray-700 leading-relaxed">{overview}</p>
            </div>
          </div>

          {/* Operational Network */}
          <div id="network-section" className="mb-10">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">{t.operationalNetwork}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-[#427d6d] p-6 rounded-lg text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-white text-gray-700 mb-3 rounded-full">
                  <MapPin />
                </div>
                <div className="text-3xl font-bold text-white">{dccwssData.dccbs_count}</div>
                <div className="text-sm font-semibold text-white mt-2">{t.dccbs}</div>
              </div>
              <div className="bg-[#427d6d] p-6 rounded-lg text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-white text-gray-700 mb-3 rounded-full">
                  <Building2 />
                </div>
                <div className="text-3xl font-bold text-white">{dccwssData.branches_count}</div>
                <div className="text-sm font-semibold text-white mt-2">{t.branches}</div>
              </div>
            </div>
          </div>

          {/* Key Statistics */}
          {keyStats.length > 0 && (
            <div id="key-stats-section" className="mb-10">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">{t.keyStats}</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {keyStats.map((stat, index) => (
                  <div
                    key={index}
                    className="bg-sky-50 border border-sky-200 p-4 rounded-lg shadow-sm text-center"
                  >
                    <div className="text-xl font-bold text-sky-700 mb-2">{stat.value}</div>
                    <p className="text-xs text-gray-600 leading-tight">
                      {pickLang(stat, 'label_eng', 'label_tamil')}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Banks List - Collapsible */}
          {banksList.length > 0 && (
            <div id="banks-list-section" className="mb-10">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 text-center">{t.dccbList}</h3>
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
                <button
                  onClick={() => setShowBanksList(!showBanksList)}
                  className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all shadow-md"
                >
                  <span className="text-lg font-semibold">{t.viewList}</span>
                  <svg
                    className={`w-6 h-6 transition-transform duration-300 ${showBanksList ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {showBanksList && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                    {banksList.map((bank, index) => (
                      <div
                        key={index}
                        className="flex items-start gap-3 p-4 bg-gradient-to-r from-blue-50 to-white rounded-lg hover:shadow-md transition-all border border-blue-100"
                      >
                        <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-white font-bold text-xs">{index + 1}</span>
                        </div>
                        <div className="flex-1">
                          <span className="text-gray-800 text-sm font-medium leading-relaxed block mb-2">
                            {bank.name}
                          </span>
                          {bank.website && bank.website !== "#" ? (
                            <button
                              onClick={() => handleBankClick(bank.website)}
                              className="inline-flex items-center text-xs text-blue-600 hover:text-blue-800 font-medium transition-colors cursor-pointer"
                            >
                              {t.visitWebsite}
                              <ExternalLink className="w-3 h-3 ml-1" />
                            </button>
                          ) : (
                            <span className="inline-flex items-center text-xs text-gray-400 font-medium">
                              {t.websiteNotAvailable}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Services Offered */}
          <div id="services-offered-section">
            <h3 className="text-lg font-semibold text-gray-800 mb-6">{t.servicesOffered}</h3>

            {/* 1. Banking Services */}
            {bankingServices.length > 0 && (
              <div className="mb-8">
                <h4 className="text-base font-semibold text-gray-800 mb-4">{t.bankingServices}</h4>
                <div className="bg-purple-50 p-6 rounded-lg">
                  <div className="space-y-3">
                    {bankingServices.map((service, index) => (
                      <div key={index} className="flex items-start">
                        <span className="text-purple-600 mr-3 flex-shrink-0">•</span>
                        <p className="text-gray-700">{pickLang(service, 'value_eng', 'value_tamil')}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* 2. Loan Products */}
            {loanProducts.length > 0 && (
              <div className="mb-8">
                <h4 className="text-base font-semibold text-gray-800 mb-4">{t.loanProducts}</h4>
                <div className="bg-amber-50 p-6 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {loanProducts.map((loan, index) => (
                      <div key={index} className="flex items-start">
                        <span className="text-amber-600 mr-3 flex-shrink-0">•</span>
                        <p className="text-gray-700">{pickLang(loan, 'value_eng', 'value_tamil')}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* 3. Other Services */}
            {(governmentSchemes.length > 0 || additionalServices.length > 0) && (
              <div className="mb-8">
                <h4 className="text-base font-semibold text-gray-800 mb-4">{t.otherServices}</h4>
                <div className="bg-blue-50 p-6 rounded-lg">
                  <div className="space-y-4">
                    {/* Government Schemes */}
                    {governmentSchemes.length > 0 && (
                      <div>
                        <h5 className="font-medium text-gray-800 mb-3">{t.governmentSchemes}</h5>
                        <div className="flex flex-wrap gap-3">
                          {governmentSchemes.map((scheme, index) => (
                            <span
                              key={index}
                              className="px-4 py-2 bg-white text-blue-700 rounded-full text-sm font-medium border border-blue-200"
                            >
                              {pickLang(scheme, 'value_eng', 'value_tamil')}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Additional Services */}
                    {additionalServices.length > 0 && (
                      <div className="pt-4 border-t border-blue-200">
                        <h5 className="font-medium text-gray-800 mb-3">{t.additionalServices}</h5>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {additionalServices.map((service, index) => (
                            <div
                              key={index}
                              className="flex items-center p-3 bg-white rounded-lg border border-blue-200"
                            >
                              <span className="text-blue-600 mr-3">✓</span>
                              <span className="text-gray-700 font-medium text-sm">
                                {pickLang(service, 'value_eng', 'value_tamil')}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

        </section>
      </div>
    </div>
  );
};

export default Dccwss;
